import { isModuleNotFoundError } from '@tanstack/router-core'
import { TRPCClientError } from '@trpc/client'
import { Component, type FunctionComponent, memo, type ReactNode, useEffect } from 'react'

import { NetworkProblemError, useTranslate } from '@orus.eu/pharaoh'

import { getMissingBrowserFeatures } from '../../lib/browser-support.ts'
import { logger } from '../../lib/logger.ts'
import { getPostHogInstance } from '../../lib/posthog-integration.ts'
import { useOpenHelpContact } from '../../modules/support/shared/help-contact-dialog/help-contact-hooks.ts'
import { HardcodedFallbackCrashPage } from '../templates/hardcoded-fallback-crash-page.tsx'

import { GenericProblemMessage } from './generic-problem-message.tsx'

type CrashPageVariant = 'technical' | 'network' | 'outdated-browser' | 'rate-limited' | 'generic-error'

/**
 * Transport-level failures: a tRPC response that never arrived, a request a component made itself
 * and saw rejected (NetworkProblemError), a chunk the router's own reload-on-module-missing
 * could not heal, or a chunk that arrived truncated. User-dependent by nature, so they are
 * deliberately kept out of the error logger (and Sentry behind it) and only recorded in PostHog,
 * with text that asks the user to check their connection and retry.
 */
function isNetworkError(error: Error): boolean {
  return (
    (error instanceof TRPCClientError && !error.data) ||
    error instanceof NetworkProblemError ||
    isModuleNotFoundError(error) ||
    isTruncatedScriptError(error)
  )
}

/**
 * V8's message for a script that ends mid-parse. JSON.parse says "Unexpected end of JSON input"
 * instead, so this only matches code the browser loaded. Chromium may prefix it with the DOM call
 * that triggered the load, such as "Failed to execute 'appendChild' on 'Node': " (OMN-2130).
 */
function isTruncatedScriptError(error: Error): boolean {
  return error instanceof SyntaxError && error.message.endsWith('Unexpected end of input')
}

/**
 * The backend rate limiter turning the user away. Expected under heavy or automated traffic, so it is
 * not a defect to report.
 */
function isRateLimitError(error: Error): boolean {
  return error instanceof TRPCClientError && error.data?.code === 'TOO_MANY_REQUESTS'
}

/**
 * A DOMException reaching the crash page means the browser's DOM layer refused an operation, which
 * happens when the DOM was altered under React (DevTools, extensions), never through our own logic.
 * Shown as a crash all the same, but kept out of the error logger like network errors.
 */
function isGenericError(error: Error): boolean {
  return error instanceof DOMException
}

function classifyCrash(error: Error): CrashPageVariant {
  // An outdated browser trumps the other classifications.
  if (getMissingBrowserFeatures().length > 0) {
    return 'outdated-browser'
  }
  if (isRateLimitError(error)) {
    return 'rate-limited'
  }
  if (isNetworkError(error)) {
    return 'network'
  }
  if (isGenericError(error)) {
    return 'generic-error'
  }
  return 'technical'
}

type CrashPageProps = {
  error: Error
  /** React's component stack, when the error was thrown while rendering. */
  componentStack?: string | undefined
}

/**
 * The screen displayed when the user hits an error we cannot recover from.
 *
 * Rendering this page IS the error report: it classifies and logs the error itself. Call sites
 * must not log the error, only pass it.
 */
export const CrashPage: FunctionComponent<CrashPageProps> = memo(function CrashPage({ error, componentStack }) {
  const variant = classifyCrash(error)

  useEffect(() => {
    if (variant === 'technical') {
      logger.error(error, { componentStack })
    }
    // Direct capture, not the tRPC-based tracking pipeline: when this page shows, our backend
    // is often the thing that's unreachable.
    getPostHogInstance()?.capture('technical_error_displayed', {
      variant,
      error_name: error.name,
      error_message: error.message,
      online: navigator.onLine,
      ...(variant === 'outdated-browser' && { missing_browser_features: getMissingBrowserFeatures().join(', ') }),
    })
  }, [error, variant, componentStack])

  return (
    <CrashPageErrorBoundary>
      <CrashPageWithContext variant={variant} />
    </CrashPageErrorBoundary>
  )
})

/**
 * Error boundary that catches any errors from CrashPageWithContext
 * and falls back to HardcodedFallbackCrashPage.
 */
class CrashPageErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  override state = { hasError: false }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return <HardcodedFallbackCrashPage />
    }
    return this.props.children
  }
}

/**
 * The actual crash page implementation that uses context hooks.
 */
const CrashPageWithContext: FunctionComponent<{ variant: CrashPageVariant }> = memo(function CrashPageWithContext({
  variant,
}) {
  const translate = useTranslate()
  const openHelpContact = useOpenHelpContact()

  switch (variant) {
    case 'outdated-browser':
      return (
        <GenericProblemMessage
          title={translate('problem_oops_title')}
          principalMessage={translate('problem_outdated_browser_principal_message')}
          firstSubText={translate('problem_outdated_browser_first_subtext')}
          buttonText={translate('contact_customer_support')}
          onButtonClick={openHelpContact}
          trackingId="contact_button"
        />
      )
    case 'rate-limited':
      return (
        <GenericProblemMessage
          title={translate('problem_oops_title')}
          principalMessage={translate('problem_rate_limit_principal_message')}
          firstSubText={translate('problem_rate_limit_first_subtext')}
          buttonText={translate('contact_customer_support')}
          onButtonClick={openHelpContact}
          trackingId="contact_button"
        />
      )
    case 'network':
      return (
        <GenericProblemMessage
          title={translate('problem_oops_title')}
          principalMessage={translate('problem_network_principal_message')}
          firstSubText={translate('problem_network_first_subtext')}
          buttonText={translate('problem_network_retry')}
          onButtonClick={() => window.location.reload()}
          trackingId="retry_button"
          secondaryButtonText={translate('contact_customer_support')}
          onSecondaryButtonClick={openHelpContact}
          secondaryButtonTrackingId="contact_button"
        />
      )
    case 'generic-error':
      return (
        <GenericProblemMessage
          title={translate('problem_oops_title')}
          principalMessage={translate('problem_generic_principal_message')}
          firstSubText={translate('problem_generic_first_subtext')}
          buttonText={translate('problem_network_retry')}
          onButtonClick={() => window.location.reload()}
          trackingId="retry_button"
          secondaryButtonText={translate('contact_customer_support')}
          onSecondaryButtonClick={openHelpContact}
          secondaryButtonTrackingId="contact_button"
        />
      )
    case 'technical':
      return (
        <GenericProblemMessage
          title={translate('problem_oops_title')}
          principalMessage={translate('problem_principal_message')}
          firstSubText={translate('problem_first_subtext')}
          secondSubText={translate('problem_second_subtext')}
          buttonText={translate('contact_customer_support')}
          onButtonClick={openHelpContact}
          trackingId="contact_button"
        />
      )
  }
})
